import { useCallback, useEffect, useRef, useState } from 'react';
import type { ColumnFiltersState } from '@tanstack/react-table';
import type { IDataTableColumnFilter, IDataTableFacetValue } from './DataTable.types';

/** Подписи значений header-фильтра: колонка → ключ → подпись. */
export type DataTableFacetLabels = Readonly<Record<string, Readonly<Record<string, string>>>>;

/** Источник значений воронки; сигнал отменяет запрос открытой воронки (у подписей его нет). */
type FacetSource = (columnId: string, signal?: AbortSignal) => IDataTableFacetValue[] | Promise<IDataTableFacetValue[]>;

const NO_LABELS: DataTableFacetLabels = {};

/**
 * Подписи отмеченных значений header-фильтра для строки активных фильтров: в фильтре лежат ключи
 * (`Critical`), а подписи («Критично») знает только источник значений. Списки, загруженные воронкой,
 * запоминаются. Ключ без подписи (пришёл из адреса, кода или сохранённого вида) с `resolveMissing`
 * запрашивается у источника один раз и только когда выборка загружена — загрузчики считают значения
 * в рамках последней выборки; до ответа, при отказе и без `resolveMissing` в чипе остаётся ключ.
 */
export function useDataTableFacetLabels({
    enabled,
    resolveMissing,
    ready,
    filters,
    source,
}: {
    /** Строка активных фильтров показана и значения даёт свой источник (у клиентских подпись = ключ). */
    enabled: boolean;
    /** Запрашивать подписи ключей, которых не было в ответах воронки. */
    resolveMissing: boolean;
    /** Выборка загружена: можно спрашивать значения. */
    ready: boolean;
    /** Применённые фильтры колонок. */
    filters: ColumnFiltersState;
    source: FacetSource;
}): {
    labels: DataTableFacetLabels;
    getFacet: FacetSource;
    /** Запомнить подписи из ответа, полученного в обход `getFacet` (порции воронки). */
    remember: (columnId: string, values: readonly IDataTableFacetValue[]) => void;
} {
    const [labels, setLabels] = useState<DataTableFacetLabels>(NO_LABELS);
    /** Ключи, подписи которых уже спрашивали: не найденный в списке ключ не перезапрашивается. */
    const requestedRef = useRef(new Map<string, Set<string>>());

    const remember = useCallback((columnId: string, values: readonly IDataTableFacetValue[]) => {
        setLabels((current) => {
            const known = current[columnId];
            let next: Record<string, string> | undefined;
            for (const { key, label } of values) {
                if (key === null || known?.[key] === label) continue;
                next ??= { ...known };
                next[key] = label;
            }
            return next ? { ...current, [columnId]: next } : current;
        });
    }, []);

    // воронка получает тот же ответ, что и раньше, — подписи из него запоминаются попутно
    const getFacet = useCallback(
        (columnId: string, signal?: AbortSignal) => {
            const result = source(columnId, signal);
            Promise.resolve(result).then(
                (values) => remember(columnId, values),
                () => undefined,
            );
            return result;
        },
        [source, remember],
    );

    useEffect(() => {
        if (!enabled || !resolveMissing || !ready) return;
        for (const { id, value } of filters) {
            const header = (value as IDataTableColumnFilter | undefined)?.header;
            if (!header?.length) continue;
            const requested = requestedRef.current.get(id) ?? new Set<string>();
            const missing = header.filter(
                (key): key is string => key !== null && labels[id]?.[key] === undefined && !requested.has(key),
            );
            if (!missing.length) continue;
            missing.forEach((key) => requested.add(key));
            requestedRef.current.set(id, requested);
            Promise.resolve()
                .then(() => source(id))
                .then(
                    (values) => remember(id, values),
                    () => undefined,
                );
        }
    }, [enabled, resolveMissing, ready, filters, labels, source, remember]);

    return { labels, getFacet: enabled ? getFacet : source, remember };
}
